import { ephemeralStore } from './ephemeralStore'
import { AccessDeniedError } from './errors'
import { ADMIN_ROLE, permissionMatches, RolePermissionMap } from './permissions'

const AUTHZ_CACHE_KEY = 'authz:v1'

function nowMs() {
  return Date.now()
}

function cacheGet(userId, accessToken) {
  const cache = ephemeralStore.getJson(AUTHZ_CACHE_KEY, {})
  const entry = cache?.[userId] || null
  if (!entry) return null
  if (entry.accessToken && accessToken && entry.accessToken !== accessToken) return null
  if (typeof entry.expiresAt !== 'number' || entry.expiresAt <= nowMs()) return null
  return entry.value || null
}

function cacheSet(userId, accessToken, value, ttlMs) {
  const cache = ephemeralStore.getJson(AUTHZ_CACHE_KEY, {})
  const next = {
    ...cache,
    [userId]: {
      accessToken: accessToken || null,
      expiresAt: nowMs() + ttlMs,
      value,
    },
  }
  ephemeralStore.setJson(AUTHZ_CACHE_KEY, next)
}

export function clearAuthorizationCache() {
  ephemeralStore.remove(AUTHZ_CACHE_KEY)
}

function normalizePermissionsJson(permissionsJson) {
  const p = permissionsJson || {}
  const out = { allow: [], deny: [] }
  if (Array.isArray(p)) {
    out.allow = p.map(String)
    return out
  }
  if (Array.isArray(p?.allow) || Array.isArray(p?.deny)) {
    out.allow = Array.isArray(p.allow) ? p.allow.map(String) : []
    out.deny = Array.isArray(p.deny) ? p.deny.map(String) : []
    return out
  }
  if (p && typeof p === 'object') {
    for (const [k, v] of Object.entries(p)) {
      if (v === true) out.allow.push(String(k))
      if (v === false) out.deny.push(String(k))
    }
  }
  return out
}

export function buildPermissionSet({ role, permissionsJson }) {
  const rolePerms = RolePermissionMap?.[role] || []
  const custom = normalizePermissionsJson(permissionsJson)
  const allow = new Set([...(rolePerms || []).map(String), ...(custom.allow || []).map(String)])
  const deny = new Set((custom.deny || []).map(String))
  return { allow, deny }
}

export function hasPermission({ role, permissionSet }, permission) {
  const needed = String(permission || '')
  if (!needed) return false
  if (String(role || '') === ADMIN_ROLE) return true
  const { allow, deny } = permissionSet || { allow: new Set(), deny: new Set() }
  for (const d of deny) {
    if (permissionMatches(d, needed)) return false
  }
  for (const a of allow) {
    if (permissionMatches(a, needed)) return true
  }
  return false
}

export function assertPermission(ctx, permission) {
  if (hasPermission(ctx, permission)) return
  throw new AccessDeniedError('Você não tem permissão para acessar esta funcionalidade.')
}

export async function fetchMyAuthorization(supabase, { ttlMs = 60_000 } = {}) {
  const { data: sessData } = await supabase.auth.getSession()
  const session = sessData?.session || null
  const userId = session?.user?.id || null
  const accessToken = session?.access_token || null
  if (!userId) return { session: null, profile: null, role: null, status: null, permissionsJson: null }

  const cached = cacheGet(userId, accessToken)
  if (cached) return { session, ...cached }

  let row = null
  let rpcError = null
  try {
    const { data, error } = await supabase.rpc('get_my_authorization')
    rpcError = error || null
    row = Array.isArray(data) ? (data[0] || null) : data || null
  } catch (e) {
    rpcError = e
    row = null
  }

  if (!row && rpcError) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, role, status, permissions, company_id, sas_partner_id, updated_at')
        .eq('id', userId)
        .maybeSingle()
      if (!error) row = data || null
    } catch {
      row = null
    }
  } else if (row && !row.full_name) {
    // Se o RPC retornou dados mas não o nome (migração pendente), buscar o nome especificamente
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .maybeSingle()
      if (!error && data?.full_name) row.full_name = data.full_name
    } catch {}
  }

  // Buscar campos extras diretamente em profiles (sempre pelo id do auth.uid()).
  if (row) {
    try {
      const { data: extra } = await supabase
        .from('profiles')
        .select('cpf, whatsapp, company_id')
        .eq('id', userId)
        .maybeSingle()
      
      if (extra) {
        row.cpf = extra.cpf || row.cpf || null
        row.whatsapp = extra.whatsapp || row.whatsapp || null
        row.company_id = extra.company_id || row.company_id || null
      }
    } catch {}

    // Compat: caso RPC já retorne company_name/companyName.
    row.companyName = row.companyName || row.company_name || null

    // Tenta resolver nome da empresa via relação explícita do profiles.company_id.
    if (!row.companyName) {
      try {
        const { data: profileWithCompany } = await supabase
          .from('profiles')
          .select('company:companies!profiles_company_id_fkey(name)')
          .eq('id', userId)
          .maybeSingle()
        const companyRel = Array.isArray(profileWithCompany?.company)
          ? profileWithCompany.company[0]
          : profileWithCompany?.company
        if (companyRel?.name) row.companyName = companyRel.name
      } catch {}
    }

    // Fallback para resolver o nome da empresa pelo company_id.
    if (!row.companyName && row.company_id) {
      try {
        const { data: companyData } = await supabase
          .from('companies')
          .select('name')
          .eq('id', row.company_id)
          .maybeSingle()
        if (companyData?.name) row.companyName = companyData.name
      } catch {}
    }

    const existingLinkedCompanies = Array.isArray(row?.linked_companies)
      ? row.linked_companies
          .map((item) => ({
            id: item?.id || item?.company_id || null,
            name: item?.name || item?.company_name || null,
          }))
          .filter((item) => item.id)
      : []
    const existingLinkedCompanyIds = Array.isArray(row?.linked_company_ids)
      ? row.linked_company_ids.filter(Boolean)
      : []
    const primaryCompany = row?.company_id
      ? {
          id: row.company_id,
          name: row.companyName || null,
        }
      : null

    try {
      const { data: profileCompanyLinks, error: profileCompanyLinksError } = await supabase
        .from('profile_company_links')
        .select('company_id')
        .eq('profile_id', userId)
      if (profileCompanyLinksError) throw profileCompanyLinksError

      const companyIds = Array.from(new Set([
        ...existingLinkedCompanyIds.map(String),
        ...(profileCompanyLinks || []).map((item) => String(item?.company_id || '')).filter(Boolean),
        ...(primaryCompany?.id ? [String(primaryCompany.id)] : []),
      ]))

      let companyNamesById = new Map(existingLinkedCompanies.map((item) => [String(item.id), item?.name || null]))
      if (companyIds.length > 0) {
        const { data: companiesData, error: companiesError } = await supabase
          .from('companies')
          .select('id, name')
          .in('id', companyIds)
        if (companiesError) throw companiesError
        companyNamesById = new Map([
          ...companyNamesById.entries(),
          ...((companiesData || []).map((item) => [String(item.id), item?.name || null])),
        ])
      }

      const linkedCompanies = companyIds.map((id) => ({
        id,
        name: companyNamesById.get(String(id)) || (String(primaryCompany?.id || '') === String(id) ? row.companyName || null : null),
      }))

      row.linked_company_ids = linkedCompanies.map((item) => item.id)
      row.linked_companies = linkedCompanies
    } catch {
      const fallbackLinkedCompanies = Array.from(
        new Map(
          [...existingLinkedCompanies, ...(primaryCompany ? [primaryCompany] : [])]
            .filter((item) => item?.id)
            .map((item) => [String(item.id), item]),
        ).values(),
      )

      row.linked_company_ids = fallbackLinkedCompanies.length > 0
        ? fallbackLinkedCompanies.map((item) => item.id)
        : (row?.company_id ? [row.company_id] : [])
      row.linked_companies = fallbackLinkedCompanies.length > 0
        ? fallbackLinkedCompanies
        : (row?.company_id ? [{ id: row.company_id, name: row.companyName || null }] : [])
    }
  }

  const role = row?.role || null
  const status = row?.status || null
  let permissionsJson = row?.permissions || null
  
  // Buscar permissões globais da role (matriz de permissões) e mesclar
  if (role) {
    try {
      const { data: roleData } = await supabase
        .from('role_permissions')
        .select('permissions')
        .eq('role', role)
        .maybeSingle()
        
      if (roleData && roleData.permissions) {
        // Merge role permissions with user specific permissions
        // User permissions override role permissions
        const userPerms = typeof permissionsJson === 'object' && !Array.isArray(permissionsJson) ? permissionsJson : {};
        permissionsJson = {
          ...(roleData.permissions || {}),
          ...userPerms
        }
      }
    } catch {}
  }

  const profile = row
    ? {
        id: row?.user_id || row?.id || userId,
        fullName: row?.full_name || null,
        role,
        status,
        permissions: permissionsJson,
        companyId: row?.company_id || null,
        companyName: row?.companyName || null,
        companyIds: Array.isArray(row?.linked_company_ids)
          ? row.linked_company_ids.filter(Boolean)
          : (row?.company_id ? [row.company_id] : []),
        linkedCompanies: Array.isArray(row?.linked_companies)
          ? row.linked_companies.filter((item) => item?.id)
          : (row?.company_id ? [{ id: row.company_id, name: row?.companyName || null }] : []),
        cpf: row?.cpf || null,
        whatsapp: row?.whatsapp || null,
        sasPartnerId: row?.sas_partner_id || null,
        updatedAt: row?.updated_at || null,
      }
    : null

  const value = { profile, role, status, permissionsJson }
  cacheSet(userId, accessToken, value, ttlMs)
  return { session, ...value }
}

export function isAdminRole(role, status) {
  return String(role || '').toLowerCase() === String(ADMIN_ROLE || '').toLowerCase() && String(status || '') !== 'Inativo'
}

export function isPartnerScopedRole(role) {
  return ['Parceiro SAS', 'Gestor Parceiro'].includes(String(role || ''))
}

export function isGestorParceiroRole(role) {
  return String(role || '') === 'Gestor Parceiro'
}

// Escopo de parâmetros por perfil (middleware leve no client)
export function scopeParamsForRole({ profileRole, profile } = {}, params = {}) {
  try {
    const p = { ...params }
    if (profileRole === 'Ponto Focal') {
      p.p_partner_id = null
      if (p.p_company_id === '') p.p_company_id = null
    } else if (isPartnerScopedRole(profileRole)) {
      p.p_partner_id = profile?.sasPartnerId || null
    }
    return p
  } catch {
    return params
  }
}

